import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Layout,
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  DatePicker,
  Select,
  InputNumber,
  Space,
  AutoComplete,
} from "antd";
import {
  PlusOutlined,
  MinusCircleOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";

import { getAPI, postAPI, displayMessage } from "../../utils/common";
import { SUCCESS_MESSAGE, ERROR_MSG_TYPE } from "../../constants/messages";
import {
  KITCHEN_ITEM_LIST,
  KITCHEN_PURCHASE_CREATE,
  KITCHEN_PR_DETAIL,
  KITCHEN_PURCHASE_LIST,
} from "../../constants/api";
import LS_SERVICE from "../../utils/localStorage";

function KitchenPurchaseAdd() {
  const { Content } = Layout;
  const navigate = useNavigate();
  const location = useLocation();
  const pr_id = location.state?.pr_id || null; // optional chaining in case state undefined

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // const [pr_data, setPRData] = useState(null);
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    fetchItems();
    getSuppliers();
    form.setFieldsValue({
      order_by: LS_SERVICE.get("name"),
    });
    if (pr_id) {
      fetchPRData();
    }
  }, []);

  const getSuppliers = () => {
    getAPI(
      KITCHEN_PURCHASE_LIST,
      (res) => {
        if (res.status && res.data) {
          // Map suppliers to options for AutoComplete
          const uniqueSuppliers = [
            ...new Map(
              res.data.map((item) => [item.supplier_name, item]),
            ).values(),
          ];
          const supplierOptions = uniqueSuppliers.map((item) => ({
            value: item.supplier_name,
            label: item.supplier_name,
          }));
          setOptions(supplierOptions);
        }
      },
      (err) => {
        console.error("Error fetching suppliers", err);
      },
      {},
    );
  };

  const fetchPRData = () => {
    setLoading(true);
    postAPI(
      KITCHEN_PR_DETAIL,
      { pr_id },
      (res) => {
        if (res.status === true) {
          const data = res.data;
          // setPRData(data);

          // Auto-fill form
          form.setFieldsValue({
            purchase_date: dayjs(), // today or you can use PR date
            supplier_name: data.supplier_name || "",
            remark: data.remark || "",
            items: data.items?.map((item) => ({
              item_id: item.item_id,
              qty: parseFloat(item.item_approved_stock || 0),
              rate: parseFloat(item.total_amount || 0).toFixed(2),
              price_type: "rate",
              item_expiry_date: null,
            })) || [{}],
          });
        } else {
          // setPRData(null);
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
  };

  const fetchItems = () => {
    getAPI(
      KITCHEN_ITEM_LIST,
      (res) => {
        if (res.status === true) {
          setItems(res.data);
        }
      },
      () => {},
    );
  };

  const createData = (values) => {
    setLoading(true);

    const payload = {
      pr_id: pr_id || null,
      pr_no: pr_id || null,
      purchase_date: dayjs(values.purchase_date).format("YYYY-MM-DD HH:mm:ss"),
      supplier_name: values.supplier_name,
      remark: values.remark,
      order_by: values.order_by,
      items: values.items.map((row) => {
        let finalRate = Number(row.rate);

        if (row.price_type === "total" && Number(row.qty) > 0) {
          finalRate = Number(row.rate) / Number(row.qty);
        }

        return {
          item_id: row.item_id,
          qty: row.qty,
          rate: finalRate,
          total_amount:
            row.price_type === "total"
              ? Number(row.rate)
              : Number(row.qty) * Number(row.rate),
          item_expiry_date: row.item_expiry_date,
        };
      }),
    };

    postAPI(
      KITCHEN_PURCHASE_CREATE,
      payload,
      (res) => {
        if (res.status === true) {
          displayMessage(SUCCESS_MESSAGE, res.message);
          navigate("/kitchen/purchase");
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
        setLoading(false);
      },
      () => {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to create purchase");
      },
    );
  };

  const watchedItems = Form.useWatch("items", form) || [];

  const grandTotal = watchedItems.reduce((sum, row) => {
    const qty = Number(row?.qty || 0);
    const value = Number(row?.rate || 0);

    if (row?.price_type === "total") {
      return sum + value;
    }

    return sum + qty * value;
  }, 0);

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Add Store Purchases"
          bordered={false}
          extra={
            <Button type="primary" ghost onClick={() => navigate(-1)}>
              <RollbackOutlined />
            </Button>
          }
        >
          <Spin spinning={loading}>
            <Form
              form={form}
              layout="vertical"
              onFinish={createData}
              initialValues={{
                items: [{}],
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Form.Item
                    name="purchase_date"
                    label="Purchase Date"
                    rules={[{ required: true }]}
                    initialValue={dayjs()}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>

                <Col span={10}>
                  <Form.Item
                    name="supplier_name"
                    label="Supplier Name"
                    rules={[
                      { required: true, message: "Please select supplier" },
                    ]}
                  >
                    <AutoComplete
                      options={options}
                      placeholder="Select or type supplier name"
                      filterOption={(inputValue, option) =>
                        option.value
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      }
                      allowClear
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name="order_by"
                    label="Order By"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Order By" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item name="remark" label="Remark">
                    <Input placeholder="Optional remark" />
                  </Form.Item>
                </Col>
              </Row>

              <Card title="Purchase Items" size="small">
                <Form.List name="items">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Row gutter={[16, 16]} key={key}>
                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, "item_id"]}
                              label="Item"
                              rules={[{ required: true }]}
                            >
                              <Select
                                placeholder="Select item"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  option?.children
                                    ?.toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                              >
                                {items.map((item) => (
                                  <Select.Option key={item.id} value={item.id}>
                                    {item.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>

                          <Col span={3}>
                            <Form.Item shouldUpdate noStyle>
                              {({ getFieldValue }) => {
                                const itemId = getFieldValue([
                                  "items",
                                  name,
                                  "item_id",
                                ]);

                                const selectedItem = items.find(
                                  (item) => item.id === itemId,
                                );

                                return (
                                  <Form.Item
                                    {...restField}
                                    name={[name, "qty"]}
                                    label="Qty"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Qty is required",
                                      },
                                      {
                                        type: "number",
                                        min: 0.001,
                                        message:
                                          "Only positive quantity allowed",
                                      },
                                    ]}
                                  >
                                    <InputNumber
                                      min={0.001}
                                      step={0.001}
                                      precision={3}
                                      suffix={
                                        selectedItem?.unit
                                          ? `(${selectedItem.unit})`
                                          : ""
                                      }
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Form.Item
                              {...restField}
                              name={[name, "price_type"]}
                              label="Type"
                              initialValue="rate"
                            >
                              <Select>
                                <Select.Option value="rate">Rate</Select.Option>

                                <Select.Option value="total">
                                  Total
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={3}>
                            <Form.Item shouldUpdate noStyle>
                              {({ getFieldValue }) => {
                                const priceType =
                                  getFieldValue([
                                    "items",
                                    name,
                                    "price_type",
                                  ]) || "rate";

                                return (
                                  <Form.Item
                                    {...restField}
                                    name={[name, "rate"]}
                                    label={
                                      priceType === "rate"
                                        ? "Rate"
                                        : "Total Amount"
                                    }
                                    rules={[
                                      {
                                        required: true,
                                        message:
                                          priceType === "rate"
                                            ? "Rate is required"
                                            : "Total amount is required",
                                      },
                                    ]}
                                  >
                                    <InputNumber
                                      min={0}
                                      precision={2}
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Form.Item shouldUpdate noStyle>
                              {({ getFieldValue }) => {
                                const qty =
                                  Number(
                                    getFieldValue(["items", name, "qty"]),
                                  ) || 0;

                                const value =
                                  Number(
                                    getFieldValue(["items", name, "rate"]),
                                  ) || 0;

                                const priceType =
                                  getFieldValue([
                                    "items",
                                    name,
                                    "price_type",
                                  ]) || "rate";

                                const total =
                                  priceType === "rate" ? qty * value : value;

                                return (
                                  <Form.Item label="Total">
                                    <Input value={total.toFixed(2)} disabled />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item
                              {...restField}
                              name={[name, "item_expiry_date"]}
                              label="Expiry Date"
                              rules={[
                                {
                                  required: true,
                                  message: "Expiry date is required",
                                },
                              ]}
                            >
                              <DatePicker
                                style={{ width: "100%" }}
                                disabledDate={(current) =>
                                  current && current < dayjs().startOf("day")
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col
                            span={4}
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Button
                              danger
                              onClick={() => remove(name)}
                              icon={<MinusCircleOutlined />}
                            />
                          </Col>
                        </Row>
                      ))}

                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                        >
                          Add Item
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Card>

              <Row>
                <Col span={24}>
                  <div
                    style={{
                      textAlign: "right",
                      fontSize: "18px",
                      fontWeight: "bold",
                      marginTop: 15,
                    }}
                  >
                    Grand Total : ₹{grandTotal.toFixed(2)}
                  </div>
                </Col>

                <Col span={24}>
                  <Form.Item style={{ float: "right", marginTop: 16 }}>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Submit Purchase
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Card>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default KitchenPurchaseAdd;
